/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, react/button-has-type, react/display-name, sort-keys */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import Slider from 'react-slick';

import classNames from 'classnames';

import 'slick-carousel/slick/slick.less';
import 'slick-carousel/slick/slick-theme.less';

import '../css/CarouselContainer.less';
import '../css/CourseCarouselRibbonTOC.less';

import { SatCoreRegister } from '../SatCoreRegistry';

import { generateRandomCryptographicId } from '../utils';

// Centralized constants for timing
const TRANSITION_TIMEOUT = 400; // Base transition timeout
const THROTTLE_TIMEOUT = 500; // Minimum time between arrow clicks
const FALLBACK_TIMEOUT = 800; // Fallback timeout to reset state

const ControlledCarouselContainer = observer((props = {}) => {
  const { navigationManager } = useContext(MobXProviderContext);
  const sliderRef = useRef(null);
  const carouselRef = useRef(null);
  const carouselId = useRef(props.carouselId || `carousel-${generateRandomCryptographicId(4)}`);

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isThrottled, setIsThrottled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideCount, setSlideCount] = useState(0);
  const [needsCentering, setNeedsCentering] = useState(false);

  // Timeout refs for cleanup
  const timeoutsRef = useRef({
    fallback: null,
    throttle: null,
    transition: null
  });

  // Extract props
  const {
    beforeChange,
    afterChange,
    onInit,
    className,
    loading = false,
    ...otherProps
  } = props;

  // Determine if interactions should be blocked
  const isBlocked = loading || isTransitioning;

  // Clean up a specific timeout
  const clearTimeout = useCallback((timeoutName) => {
    if (timeoutsRef.current[timeoutName]) {
      window.clearTimeout(timeoutsRef.current[timeoutName]);
      timeoutsRef.current[timeoutName] = null;
    }
  }, []);

  // Clean up all timeouts
  const clearAllTimeouts = useCallback(() => {
    Object.keys(timeoutsRef.current).forEach((key) => clearTimeout(key));
  }, [clearTimeout]);

  // Update centering based on slide count vs slidesToShow
  useEffect(() => {
    const childCount = React.Children.count(props.children);
    const slidesToShow = props.slidesToShow || 6;
    setNeedsCentering(childCount < slidesToShow);
  }, [props.children, props.slidesToShow]);

  // Emit custom events on mount and unmount
  useEffect(() => {
    // Notify the observer about carousel being added
    document.dispatchEvent(new CustomEvent('controlled-carousel:added', {
      detail: { id: carouselId.current }
    }));

    // Cleanup function
    return () => {
      // Notify the observer about carousel being removed
      document.dispatchEvent(new CustomEvent('controlled-carousel:removed', {
        detail: { id: carouselId.current }
      }));
      // Clean up any remaining timeouts
      clearAllTimeouts();
    };
  }, [clearAllTimeouts]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!carouselRef?.current) {
      return;
    }
    const handleKeyDown = (e) => {
      if (!e || e.key === 'Tab' || loading) {
        return;
      }
      const activeButton = e.target.closest('button');
      if (activeButton && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        activeButton.click();
      }
    };
    carouselRef.current.addEventListener('keydown', handleKeyDown);
    return () => {
      if (carouselRef.current) {
        carouselRef.current.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [loading]);

  // Set slide count on initialization
  const handleInit = useCallback((slick) => {
    const childCount = React.Children.count(props.children);
    setSlideCount(childCount);
    if (onInit) onInit(slick);
  }, [props.children, onInit]);

  // Handle transition start
  const handleBeforeChange = useCallback((oldIndex, newIndex) => {
    // Clear any existing timeouts
    clearTimeout('transition');
    clearTimeout('fallback');

    setIsTransitioning(true);
    if (beforeChange) beforeChange(oldIndex, newIndex);

    // Set fallback timeout to reset transitioning state if afterChange doesn't fire
    timeoutsRef.current.fallback = window.setTimeout(() => {
      setIsTransitioning(false);
    }, FALLBACK_TIMEOUT);
  }, [beforeChange, clearTimeout]);

  // Handle transition end
  const handleAfterChange = useCallback((index) => {
    // Clear fallback timeout as we're handling the transition end correctly
    clearTimeout('fallback');

    // Update current slide
    setCurrentSlide(index);

    // Set timeout to end transition state
    timeoutsRef.current.transition = window.setTimeout(() => {
      setIsTransitioning(false);
    }, TRANSITION_TIMEOUT);

    if (afterChange) afterChange(index);
  }, [afterChange, clearTimeout]);

  // Function to handle direct slide clicks
  const handleSlideClick = useCallback((index) => {
    // Do not process clicks if blocked
    if (isBlocked) {
      return;
    }
    // Force immediate navigation
    if (sliderRef?.current) {
      // Clear existing timeouts to prevent interference
      clearAllTimeouts();
      // Navigate to the slide
      setTimeout(() => {
        if (sliderRef?.current) {
          sliderRef.current.slickGoTo(index);
          // Find and focus the slide button after navigation
          setTimeout(() => {
            const activeSlide = carouselRef?.current?.querySelector(
              `.slick-slide[data-index="${index}"]`
            );
            if (activeSlide) {
              const button = activeSlide.querySelector('button');
              if (button) {
                button.focus();
                button.blur();
              }
            }
          }, 50);
        }
      }, 0);
    }
  }, [isBlocked, clearAllTimeouts]);

  // Throttle function for arrow clicks
  const throttleArrows = useCallback(() => {
    clearTimeout('throttle');
    setIsThrottled(true);

    timeoutsRef.current.throttle = window.setTimeout(() => {
      setIsThrottled(false);
    }, THROTTLE_TIMEOUT);
  }, [clearTimeout]);

  // Methods to control the carousel
  const goToSlide = useCallback((index) => {
    if (sliderRef?.current && !loading) {
      if (isTransitioning) {
        setIsTransitioning(false);
      }
      sliderRef.current.slickGoTo(index);
    }
  }, [isTransitioning, loading]);

  const goToNext = useCallback(() => {
    if (sliderRef?.current && !loading && !isThrottled) {
      if (isTransitioning) {
        setIsTransitioning(false);
      }
      throttleArrows();
      sliderRef.current.slickNext();
    }
  }, [isTransitioning, loading, isThrottled, throttleArrows]);

  const goToPrev = useCallback(() => {
    if (sliderRef?.current && !loading && !isThrottled) {
      if (isTransitioning) {
        setIsTransitioning(false);
      }
      throttleArrows();
      sliderRef.current.slickPrev();
    }
  }, [isTransitioning, loading, isThrottled, throttleArrows]);

  // Custom arrow components with simplified handling
  const PrevArrow = useMemo(() => {
    return (arrowProps) => {
      const { className } = arrowProps;
      const handleClick = (e) => {
        if (loading || isThrottled) {
          e.preventDefault();
          return;
        }
        throttleArrows();
        if (sliderRef.current) {
          const newIndex = currentSlide - 1;
          if (newIndex >= 0) {
            sliderRef.current.slickGoTo(newIndex);
          }
        }
      };
      const buttonClass = classNames(className, {
        disabled: loading
      });
      return (
        <button
          aria-label='Previous slide'
          className={buttonClass}
          onClick={handleClick}
          type='button' />
      );
    };
  }, [loading, isThrottled, throttleArrows, currentSlide, props.children]);

  const NextArrow = useMemo(() => {
    return (arrowProps) => {
      const { className } = arrowProps;
      const handleClick = (e) => {
        if (loading || isThrottled) {
          e.preventDefault();
          return;
        }
        throttleArrows();
        if (sliderRef.current) {
          const childCount = React.Children.count(props.children);
          const newIndex = currentSlide + 1;
          if (newIndex < childCount) {
            sliderRef.current.slickGoTo(newIndex);
          }
        }
      };
      const buttonClass = classNames(className, {
        disabled: loading
      });
      return (
        <button
          aria-label='Next slide'
          className={buttonClass}
          onClick={handleClick}
          type='button' />
      );
    };
  }, [loading, isThrottled, throttleArrows, currentSlide, props.children]);

  // Render slide item with consistent handling
  const renderSlideItem = useCallback((child, index) => {
    return (
      <div
        className={classNames(
          'carousel-slide', {
            'slide-infinite': infiniteCarouselSlides,
            'slide-transitioning': isBlocked
          }
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (isBlocked) {
            e.preventDefault();
            return;
          }
          handleSlideClick(index);
        }}
        role='tabpanel'>
        {React.cloneElement(child, {
          'className': classNames(child?.props?.className || '', {
            'carousel-item-transitioning': isBlocked
          }),
          'onClick': (e) => {
            e.stopPropagation();
            if (isBlocked) {
              e.preventDefault();
              return;
            }
            handleSlideClick(index);
            if (child.props.onClick) {
              child.props.onClick(e);
            }
          },
          'style': {
            ...child.props.style,
            outline: 'none'
          },
          'data-slide-index': index,
          'aria-disabled': isBlocked ? 'true' : 'false'
        })}
      </div>
    );
  }, [isBlocked, handleSlideClick]);

  // Expose control methods through ref
  React.useImperativeHandle(props.controlRef, () => ({
    goToSlide,
    goToNext,
    goToPrev,
    getCarouselId: () => carouselId.current,
    getCurrentSlide: () => currentSlide,
    getSlideCount: () => slideCount,
    isTransitioning: () => isTransitioning,
    isLoading: () => loading,
    isArrowsThrottled: () => isThrottled
  }), [goToSlide, goToNext, goToPrev, currentSlide, slideCount, isTransitioning, loading, isThrottled]);

  const infiniteCarouselSlides = needsCentering ? false : (otherProps.infinite !== false);

  // Slider settings with memoization
  const settings = useMemo(() => ({
    arrows: true,
    dots: false,
    focusOnSelect: true,
    slidesToShow: 6,
    speed: 300,
    swipe: false,
    ...otherProps,
    beforeChange: handleBeforeChange,
    afterChange: handleAfterChange,
    init: handleInit,
    adaptiveHeight: navigationManager.allowCarouselAdaptiveHeight,
    centerPadding: '0px',
    cssEase: 'linear',
    infinite: infiniteCarouselSlides,
    variableWidth: needsCentering,
    waitForAnimate: true,
    slidesToScroll: 1,
    touchThreshold: 10,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    accessibility: false,
    dotsClass: 'slick-dots keyboard-accessible',
    customPaging(i) {
      return (
        <button
          aria-label={`Go to slide ${i + 1}`}
          aria-selected={currentSlide === i}
          role='tab'
          tabIndex={0}>
          {props.customPaging ? props.customPaging(i) : (i + 1)}
        </button>
      );
    }
  }), [
    otherProps, handleBeforeChange, handleAfterChange, handleInit,
    navigationManager.allowCarouselAdaptiveHeight, needsCentering,
    PrevArrow, NextArrow, currentSlide, props.customPaging
  ]);

  // Container className with memoization
  const containerClassName = useMemo(() => classNames(
    'carousel-container',
    'controlled-carousel-container', {
      'few-slides': needsCentering,
      'is-arrows-throttled': isThrottled,
      'is-loading': loading,
      'is-transitioning': isTransitioning
    }, className
  ), [needsCentering, isTransitioning, loading, isThrottled, className]);

  // ARIA attributes
  const ariaProps = {
    'aria-label': props['aria-label'] || 'Carousel',
    'aria-roledescription': 'carousel',
    'role': 'region',
    'tabIndex': 0
  };

  return (
    <div
      ref={carouselRef}
      className={containerClassName}
      data-carousel-id={carouselId.current}
      {...ariaProps}>
      <Slider
        ref={sliderRef}
        {...settings}>
        {React.Children.map(props.children, (child, index) => renderSlideItem(child, index))}
      </Slider>
    </div>
  );
});

// Add prop for external control
ControlledCarouselContainer.defaultProps = {
  controlRef: { current: null }
};

export default ControlledCarouselContainer;

SatCoreRegister('ControlledCarouselContainer', ControlledCarouselContainer);
